/**
 * Comprobaciones de una calibración individual contra su propia muestra.
 *
 * **Desde el ADR-009 es la guarda de los perfiles de UN vehículo.** Un umbral de
 * toda la cadencia se verifica contra la flota de esa cadencia
 * (`fleet-coverage.ts`): el p99 de un vehículo no dice si un umbral es normal
 * para todos, y la simulación del 06/10/2026 mostró que en las dos direcciones
 * se equivoca —rechaza el umbral que 58 de 63 vehículos alcanzan, y acepta
 * cualquiera si el de referencia es brusco—.
 *
 * ## Qué pasó sin esta guarda
 *
 * Dos calibraciones llegaron a estar activas en producción con umbrales que
 * ningún tramo de su propia muestra podía alcanzar:
 *
 *   LOW v15     aceleración 1,15 m/s²  ·  p99 del vehículo 0,45
 *   MEDIUM v12  aceleración 2,5 m/s²   ·  p99 del vehículo 1,367
 *
 * Un umbral así no produce un informe estricto: produce un informe en ceros, y
 * un informe en ceros **se lee como «maneja perfecto»** cuando lo que dice en
 * realidad es «no medimos» (ADR-007, y el Artículo 3 de la constitución lo
 * prohíbe con todas sus letras). Las dos se activaron sin que nada avisara.
 *
 * ## Dos reglas, no una
 *
 * ### 1. El p99 de los tres umbrales de detección (orientativo)
 *
 * Se compara el umbral de cada familia contra el **p99 de la misma muestra con
 * la que se calibró**. Desde ADR-015, quedar por encima del p99 es una
 * advertencia estadística y no veta el guardado manual. Igual, por encima o por
 * debajo se conservan el percentil, la muestra, la proporción y los episodios.
 *
 * **Por encima del p99 no es lo mismo que cero.** Hasta el 07/10/2026 el aviso
 * decía «no dispararía ningún episodio», y es falso por construcción: por encima
 * del p99 queda hasta el 1 % de la muestra, y el detector las cuenta. En el
 * Laboratorio salió un rechazo así junto a once frenadas del mismo vehículo.
 *
 * **Y cero mediciones tampoco es cero episodios.** El giro puede disparar por
 * aceleración lateral sin pasar el umbral angular que se compara aquí, y la
 * muestra va redondeada a dos decimales mientras el detector compara sin
 * redondear (0,124 dispara con 0,123; en la muestra es 0,12). Así que el aviso
 * dice cuántas mediciones llegan al umbral (`aboveThreshold`) y, aparte, lo que
 * cuenta el motor (`episodes`) si el llamador lo pasó. Sin conteo del motor no
 * afirma nada sobre los episodios. Ninguno de esos conteos convierte la
 * advertencia p99 en veto.
 *
 * El percentil sale de `describe()` de `calibration-lab.ts`, la MISMA función
 * con la que el laboratorio dibuja las distribuciones y propone umbrales, y
 * sobre la MISMA muestra (`familySamples`). No se calcula aquí otra vez: si el
 * número orientativo fuera distinto del que se ve en pantalla, el analista no
 * podría reproducir la advertencia.
 *
 * ### 2. ¿Habría podido disparar cada familia? (las cuatro, y sus compuertas)
 *
 * El p99 cubría tres familias de cuatro y ningún criterio secundario, y el
 * esquema de `api.ts` deja escribir tres configuraciones que dejan un detector
 * mudo sin que el p99 se entere:
 *
 *   minimumHeadingChangeDegrees = 179  → a 10 s hacen falta 17,9 °/s
 *   minimumSpeedChangeKmh = 100        → a 10 s hacen falta 2,78 m/s²
 *   operationalSpeedLimitKmh = 120     → en un urbano que no pasa de 55 km/h
 *
 * Las tres son la misma falla de siempre —un cero que significa «no se pudo
 * medir» publicado como «no ocurrió»— y la tercera se lleva el 35 % del peso
 * del puntaje, que es el pedazo más grande.
 *
 * La pregunta se la hace `assessFiringChance` de `report/component-measurability.ts`,
 * que es la que el informe ya se hace al publicar: *con estos umbrales y esta
 * muestra, ¿hubo al menos un par en que el valor superó lo que le tocaba a ese
 * par?* Aquí no se reimplementa: se llama. Par a par y sin percentiles, porque
 * resumir la muestra antes de compararla ya devolvió el informe falso dos veces
 * (la cabecera de ese módulo cuenta las dos).
 *
 * La compuerta secundaria se comprueba además **por su cuenta**
 * (`gateAlone`): el detector la exige siempre, así que ningún par que la pase
 * significa familia muda incluso cuando el umbral no gobierna —el caso de la
 * aceleración lateral encendida, donde la pregunta del umbral se abstiene—.
 *
 * ### Por qué el p99 NO se le aplica a la velocidad alta
 *
 * `operationalSpeedLimitKmh` no es un percentil de nada: es una política («en
 * esta flota no se pasa de 80»). Exigirle que quede bajo el p99 de los tramos
 * obligaría a que todo vehículo calibrado tenga siempre un 1 % de tramos en
 * exceso, que es una afirmación sobre la conducción y no sobre la medición. La
 * barra correcta para una política es la otra: que el vehículo **llegue** a ese
 * límite alguna vez en el período, porque si no, su 35 % del puntaje no se mide.
 *
 * ## Muestra insuficiente
 *
 * `MINIMUM_SAMPLE` (500) es la barra para que un percentil **describa a la
 * flota**, y con ella se decide si una propuesta del laboratorio es `usable`.
 * Aquí la pregunta es otra y más modesta: ¿este umbral está por encima de todo
 * lo que este vehículo hizo? Para eso basta con que haya mediciones.
 *
 * Así que la decisión es: **el tamaño de la muestra cambia lo que la guarda
 * DICE, nunca si bloquea.**
 *
 *  - Sin ninguna medición de una familia (`count === 0`) se rechaza: un umbral
 *    sin una sola medición detrás es precisamente lo que esto existe para
 *    impedir, y no hay p99 que comparar.
 *  - Con menos de `MINIMUM_SAMPLE` la guarda compara igual y lo advierte en el
 *    texto. La alternativa descartada era rechazar toda calibración con muestra
 *    corta, y se descartó porque los vehículos que más necesitan umbrales
 *    propios son justamente los urbanos de poco kilometraje —DEVICE-REF-0021 con 424 km,
 *    DEVICE-REF-0020 con 592— y esa regla los dejaría sin poder calibrarse nunca. Además
 *    los dos casos que había que atajar están errados por un factor 2,5 y 1,8,
 *    no por el error de estimación de un percentil.
 *
 * ## La entrada es el perfil y las mediciones del motor
 *
 * No unos arreglos sueltos. Las dos reglas necesitan más que los tres umbrales
 * de detección —el límite de velocidad, las compuertas, el intervalo de cada
 * par— y recibir el perfil completo y `CandidateMeasurements` es lo que hace
 * imposible que un llamador arme «su» muestra: es la que produjo el motor.
 * Armarla a mano desde `inspectDriving().pairs` fue exactamente el hallazgo A.
 *
 * Todo aquí es puro: mismas mediciones y mismo perfil, mismo veredicto y mismas
 * advertencias.
 */

import type { CandidateMeasurements } from "../pipeline/driving-analysis-v5.ts";
import type { AnalysisProfile } from "../pipeline/profiles.ts";
import {
  assessFiringChance,
  COMPONENT_ORDER,
  FAMILY_UNITS,
  type FamilyFiringChance,
} from "../report/component-measurability.ts";
import type { ScoreComponent } from "../report/drivescore-v1.ts";
import { describe as describeDistribution, familySamples, MINIMUM_SAMPLE } from "./calibration-lab.ts";

/** Percentil de la guarda. Es el techo de la muestra, no el umbral propuesto. */
export const GUARD_PERCENTILE = 99;

export type GuardFamily = "aceleracion" | "frenada" | "giro";

export type FamilyCheck = {
  family: GuardFamily;
  /** Nombre para el mensaje. En español, como lo lee quien calibra. */
  label: string;
  unit: string;
  /** Umbral pedido, siempre positivo: la frenada se compara en magnitud. */
  threshold: number;
  /** p99 de la muestra, `null` si no hubo mediciones. */
  percentileValue: number | null;
  sampleCount: number;
  /**
   * Cuántas mediciones de la muestra llegan al umbral. **No es una cota de los
   * episodios, ni hacia arriba ni hacia abajo**: el detector exige además las
   * compuertas secundarias y la consolidación junta candidatos (menos), pero el
   * giro puede disparar por aceleración lateral sin pasar el umbral angular, y
   * la muestra va redondeada a dos decimales mientras el detector compara sin
   * redondear (más). Cuántos episodios hay lo dice `episodes`, el motor.
   */
  aboveThreshold: number;
  /**
   * Episodios que el motor cuenta en la familia con el perfil pedido, si el
   * llamador los pasó. Mediciones y episodios no son lo mismo: dos frenadas en
   * pares contiguos son dos mediciones y un episodio.
   */
  episodes: number | null;
  /** La muestra alcanza para comparar pero no llega a `MINIMUM_SAMPLE`. */
  belowMinimumSample: boolean;
  verdict: "OK" | "ABOVE_PERCENTILE" | "NO_MEASUREMENTS";
  /** Qué pasó, con los cuatro números. Un rechazo mudo obliga a adivinar. */
  explanation: string;
};

/** El veredicto par a par de una familia, ya redactado. */
export type FiringCheck = {
  component: ScoreComponent;
  label: string;
  unit: string;
  /**
   * `MUTE` es lo único que bloquea. `UNDECIDED` es una abstención declarada: no
   * se puede afirmar que la familia quede muda, y bloquear «por si acaso»
   * dejaría sin guardar al preset del manual, donde el giro se decide por
   * aceleración lateral y la velocidad angular deja de gobernar.
   */
  verdict: "OK" | "MUTE" | "UNDECIDED";
  chance: FamilyFiringChance;
  explanation: string;
};

export type GuardResult = {
  ok: boolean;
  /** El p99 de los tres umbrales de detección. */
  checks: FamilyCheck[];
  /** Desviaciones estadísticas no bloqueantes: umbral por encima del p99. */
  warnings: FamilyCheck[];
  /** Fallos de procedencia técnica: no existe ninguna medición de la familia. */
  rejected: FamilyCheck[];
  /** La pregunta par a par, las cuatro familias. */
  firing: FiringCheck[];
  rejectedFiring: FiringCheck[];
  /** Todas las explicaciones que bloquean, en orden, para pintarlas una a una. */
  reasons: string[];
  /** Todo junto en una línea, listo para la respuesta HTTP. */
  message: string | null;
};

const round3 = (value: number) => Math.round(value * 1000) / 1000;
const es = (value: number) => value.toLocaleString("es-CO", { maximumFractionDigits: 3 });

/** Nombre de cada familia en la guarda, como lo lee quien calibra. */
const FAMILY_LABELS: Record<ScoreComponent, string> = {
  velocidadAlta: "Velocidad alta",
  frenadas: "Frenada",
  aceleraciones: "Aceleración",
  giros: "Giro",
};

/**
 * Cómo se nombra «lo más alto observado» en cada familia.
 *
 * La velocidad alta se mide por tramo y las otras tres por par, así que un
 * texto único diría de una de las dos algo que no es. El aviso va a una
 * pantalla donde el analista tiene esa distinción delante.
 */
const OBSERVED_PHRASE: Record<ScoreComponent, string> = {
  velocidadAlta: "la velocidad de tramo más alta del período fue",
  frenadas: "lo más fuerte que este vehículo frenó fue",
  aceleraciones: "lo más fuerte que este vehículo aceleró fue",
  giros: "lo más rápido que este vehículo giró fue",
};

/** Qué unidad de medición mira cada familia: el tramo suelto o el par. */
const UNIT_OF_ANALYSIS: Record<ScoreComponent, string> = {
  velocidadAlta: "tramo",
  frenadas: "par",
  aceleraciones: "par",
  giros: "par",
};

function checkFamily(
  family: GuardFamily,
  label: string,
  unit: string,
  threshold: number,
  values: number[],
  episodes: number | null,
): FamilyCheck {
  // `describe()` ya devuelve el p99 redondeado y el tamaño de la muestra, y es
  // el mismo número que el laboratorio dibuja en la tira de distribución.
  const distribution = describeDistribution(values);
  const percentileValue = distribution.percentiles[String(GUARD_PERCENTILE)] ?? null;
  const sampleCount = distribution.count;
  // Misma comparación que el detector: la aceleración dispara con `>=` y la
  // frenada con `<=` sobre el valor con signo, que en magnitud es `>=`.
  const aboveThreshold = values.filter((value) => value >= threshold).length;
  const belowMinimumSample = sampleCount > 0 && sampleCount < MINIMUM_SAMPLE;
  const corta = belowMinimumSample
    ? ` Ojo: la muestra tiene ${sampleCount} mediciones, menos de las ${MINIMUM_SAMPLE} que se piden para` +
      ` que un percentil describa a una flota; para saber si el umbral se sale del rango de ESTE vehículo alcanza.`
    : "";

  if (sampleCount === 0 || percentileValue === null) {
    return {
      family,
      label,
      unit,
      threshold: round3(threshold),
      percentileValue: null,
      sampleCount,
      aboveThreshold: 0,
      episodes,
      belowMinimumSample: false,
      verdict: "NO_MEASUREMENTS",
      explanation:
        `${label}: el período no dejó ni una medición, así que no hay con qué comprobar el umbral de ` +
        `${es(round3(threshold))} ${unit}. Un umbral sin muestra detrás es un número sin procedencia.`,
    };
  }

  if (threshold > percentileValue) {
    // Qué deja el umbral. Las mediciones se dicen con su número; los episodios,
    // solo si los contó el motor. La muestra no basta para afirmar cero: el giro
    // puede disparar por aceleración lateral, y la muestra va redondeada.
    const mediciones =
      aboveThreshold === 0
        ? `Ninguna medición de la muestra llega a ese umbral.`
        : `Solo ${aboveThreshold} de ${sampleCount} mediciones ` +
          `(${es(round3((aboveThreshold / sampleCount) * 100))} %) llegan a ese umbral.`;
    const motor =
      episodes === null
        ? `Cuántos episodios saldrían lo dice el motor, no esta cuenta: la guarda no lo afirma.`
        : episodes === 0
          ? `Con estos umbrales el motor no cuenta episodios de esta familia en esta muestra. Cero episodios ` +
            `no invalida por sí solo un umbral manual.`
          : `Con estos umbrales el motor cuenta ${episodes} ${episodes === 1 ? "episodio" : "episodios"} de ` +
            `esta familia en esta muestra.`;
    const consecuencia =
      `${mediciones} ${motor} El umbral deja fuera más del ${GUARD_PERCENTILE} % de las mediciones de este ` +
      `vehículo; se muestra como advertencia estadística y no bloquea el guardado manual.`;
    return {
      family,
      label,
      unit,
      threshold: round3(threshold),
      percentileValue,
      sampleCount,
      aboveThreshold,
      episodes,
      belowMinimumSample,
      verdict: "ABOVE_PERCENTILE",
      explanation:
        `${label}: el umbral de ${es(round3(threshold))} ${unit} queda por encima del p${GUARD_PERCENTILE} ` +
        `de la muestra, que es ${es(percentileValue)} ${unit} sobre ${sampleCount} mediciones. ` +
        `${consecuencia}${corta}`,
    };
  }

  return {
    family,
    label,
    unit,
    threshold: round3(threshold),
    percentileValue,
    sampleCount,
    aboveThreshold,
    episodes,
    belowMinimumSample,
    verdict: "OK",
    explanation:
      `${label}: ${es(round3(threshold))} ${unit} está dentro del rango observado ` +
      `(p${GUARD_PERCENTILE} = ${es(percentileValue)} ${unit} sobre ${sampleCount} mediciones).${corta}`,
  };
}

/** El cierre común de todo aviso de familia muda: por qué importa. */
const MUTE_TAIL =
  "Un detector que no puede dispararse publica un cero que se lee como «no ocurrió», " +
  "y se lleva su peso entero del puntaje.";

function explainMute(chance: FamilyFiringChance, profile: AnalysisProfile): string {
  const label = FAMILY_LABELS[chance.component];
  const unit = FAMILY_UNITS[chance.component];
  const observed = `${OBSERVED_PHRASE[chance.component]} ${es(chance.observedMax ?? 0)} ${unit}`;
  const sobre = `sobre ${chance.observedCount} mediciones`;
  const ninguno = `ningún ${UNIT_OF_ANALYSIS[chance.component]} del período`;

  if (chance.belowReferenceSpeed) {
    return (
      `${label}: la velocidad mínima de referencia de ${es(profile.turns.minimumReferenceSpeedKmh)} km/h ` +
      `queda por encima de la velocidad más alta con la que este vehículo tomó una curva en el período ` +
      `(${es(chance.referenceSpeedMax ?? 0)} km/h), así que ningún giro cuenta, por muy bajo que sea el ` +
      `umbral angular. ${MUTE_TAIL}`
    );
  }

  if (chance.observedCount === 0) {
    return (
      `${label}: el período no dejó ni una medición de esta familia, así que no hay con qué comprobar ` +
      `que el detector pueda dispararse. ${MUTE_TAIL}`
    );
  }

  // Cuando la que bloquea es una compuerta secundaria se la nombra con SU
  // número y con el intervalo que la traduce. Decir solo «ningún par llegó al
  // umbral» mandaría a bajar el umbral, que no es la perilla que bloquea.
  if (chance.gateKind !== null && (chance.gateBinds || chance.gateAlone === false)) {
    const perilla =
      chance.gateKind === "heading_change_degrees"
        ? `el cambio de rumbo mínimo de ${es(chance.gateValue ?? 0)}°`
        : `el cambio mínimo de velocidad de ${es(chance.gateValue ?? 0)} km/h`;
    const exige =
      chance.requirementAtObservedMax !== null && chance.intervalAtObservedMax !== null
        ? `exige ${es(chance.requirementAtObservedMax)} ${unit} en los ` +
          `${es(chance.intervalAtObservedMax)} s que hay entre los dos puntos de ese par`
        : `no se alcanza en ${ninguno}`;
    return (
      `${label}: ${perilla} ${exige}, y ${observed} ${sobre}. Esa compuerta deja el detector mudo ` +
      `pase lo que pase con el umbral. ${MUTE_TAIL}`
    );
  }

  return (
    `${label}: con el umbral de ${es(chance.threshold ?? chance.requirementAtObservedMax ?? 0)} ${unit} ` +
    `${ninguno} habría podido dispararse — ${observed} ${sobre}. ${MUTE_TAIL}`
  );
}

function checkFiring(chance: FamilyFiringChance, profile: AnalysisProfile): FiringCheck {
  const label = FAMILY_LABELS[chance.component];
  const unit = FAMILY_UNITS[chance.component];

  // `gateAlone === false` basta para afirmar muda: la compuerta es una
  // condición que el detector exige además del umbral, así que no depende de
  // que el umbral gobierne.
  const mute = chance.belowReferenceSpeed || chance.couldFire === false || chance.gateAlone === false;
  if (mute) {
    return { component: chance.component, label, unit, verdict: "MUTE", chance, explanation: explainMute(chance, profile) };
  }

  if (chance.couldFire === null) {
    const porque =
      profile.turns.lateralAccelerationMps2 > 0 && chance.component === "giros"
        ? `con la aceleración lateral encendida (${es(profile.turns.lateralAccelerationMps2)} m/s²) hay un ` +
          `segundo camino para disparar un giro, así que la velocidad angular no gobierna la familia`
        : `no llegó el intervalo de cada par, así que la compuerta secundaria no se puede traducir a ${unit}`;
    return {
      component: chance.component,
      label,
      unit,
      verdict: "UNDECIDED",
      chance,
      explanation: `${label}: ${porque}, y la guarda no puede afirmar que el detector quede mudo.`,
    };
  }

  return {
    component: chance.component,
    label,
    unit,
    verdict: "OK",
    chance,
    explanation:
      `${label}: al menos un ${UNIT_OF_ANALYSIS[chance.component]} del período habría podido ` +
      `dispararse con estos umbrales ` +
      `(${OBSERVED_PHRASE[chance.component]} ${es(chance.observedMax ?? 0)} ${unit}, y en ese par el ` +
      `detector exigía ${es(chance.requirementAtObservedMax ?? 0)} ${unit}).`,
  };
}

/**
 * Comprueba los umbrales que se quieren activar contra la muestra que los midió.
 *
 * `profile` es el perfil exacto que va a correr —`applyThresholds` sobre el del
 * código de esa cadencia— y `measurements` lo que midió `measureCandidateValues`
 * sobre el período con el que se calibró.
 *
 * Las bandas de severidad se validan aparte, contra el umbral de detección, y
 * esa regla ya existe en la ruta de guardado.
 */
export function checkThresholdsAgainstSample(input: {
  profile: AnalysisProfile;
  measurements: CandidateMeasurements;
  /**
   * Episodios por familia que el motor cuenta con `profile` sobre la misma
   * muestra (`countEpisodesByFamily`). Opcional: con ellos el aviso dice la
   * cifra real del perfil pedido y no solo la cota de mediciones.
   */
  episodes?: Partial<Record<ScoreComponent, number>>;
}): GuardResult {
  const { profile, measurements } = input;
  const episodesOf = (component: ScoreComponent) => input.episodes?.[component] ?? null;
  const samples = familySamples(measurements);

  const checks: FamilyCheck[] = [
    checkFamily(
      "aceleracion",
      FAMILY_LABELS.aceleraciones,
      FAMILY_UNITS.aceleraciones,
      profile.longitudinal.accelerationThresholdMps2,
      samples.acceleration,
      episodesOf("aceleraciones"),
    ),
    // La frenada se guarda con signo negativo y se compara en magnitud: es la
    // misma convención con la que el laboratorio la dibuja y la propone.
    checkFamily(
      "frenada",
      FAMILY_LABELS.frenadas,
      FAMILY_UNITS.frenadas,
      Math.abs(profile.longitudinal.brakingThresholdMps2),
      samples.braking,
      episodesOf("frenadas"),
    ),
    checkFamily(
      "giro",
      FAMILY_LABELS.giros,
      FAMILY_UNITS.giros,
      profile.turns.minimumYawRateDegreesPerSecond,
      samples.yaw,
      episodesOf("giros"),
    ),
  ];

  const chances = assessFiringChance(measurements, profile);
  const firing = COMPONENT_ORDER.map((component) => checkFiring(chances[component], profile));

  const warnings = checks.filter((check) => check.verdict === "ABOVE_PERCENTILE");
  const rejected = checks.filter((check) => check.verdict === "NO_MEASUREMENTS");
  const rejectedFiring = firing.filter((check) => check.verdict === "MUTE");

  // Una familia sin mediciones no se explica dos veces: el primer bloqueo trae
  // los cuatro números y el de alcanzabilidad diría lo mismo con otras palabras.
  const yaDicha = new Set(rejected.map((check) => FAMILY_OF[check.family]));
  const reasons = [
    ...rejected.map((check) => check.explanation),
    ...rejectedFiring
      .filter((check) => !yaDicha.has(check.component))
      .map((check) => check.explanation),
  ];

  const ok = rejected.length === 0 && rejectedFiring.length === 0;

  return {
    ok,
    checks,
    warnings,
    rejected,
    firing,
    rejectedFiring,
    reasons,
    message: ok
      ? null
      : `La calibración no se guardó porque ${reasons.length === 1 ? "un umbral no está" : "hay umbrales que no están"} ` +
        `respaldado${reasons.length === 1 ? "" : "s"} por la muestra con la que se calibró. ` +
        reasons.join(" "),
  };
}

/** La familia del puntaje que le corresponde a cada familia del p99. */
const FAMILY_OF: Record<GuardFamily, ScoreComponent> = {
  aceleracion: "aceleraciones",
  frenada: "frenadas",
  giro: "giros",
};
